import type { ProductCategoryId } from '../constants'
import type { Language, Market } from '../schemas/simulation'
import type { Mode } from '../taxonomy'
import type { CertGoals, HalalMarket } from './targets'
import { DEFAULT_HALAL_MARKET } from './targets'
import {
  HALAL_TERMS,
  matchTerm,
  splitIngredientUnits,
  textHasAny,
  textTermHits,
  type IngredientTerm,
} from './terms'

/**
 * 코드 검사가 읽는 제품 사실 (2026-11-01). 결과(SimulationPublic)에서 검사에 필요한 칸만 받고, 한 번만 계산해 규칙마다 나눠 쓴다.
 * AI 를 부르지 않는 순수 함수라 서버(생성 게이트·저장 재계산)와 앱(테스트)이 같은 값을 얻는다.
 */
export interface CertSubject {
  language: Language
  market: Market
  mode: Mode
  product: {
    name: string
    tagline: string
    categoryId: ProductCategoryId
    subtype: string
    format: string
    storage: 'ambient' | 'refrigerated' | 'frozen'
    shelfLifeDays: number
    howToUse?: string
  }
  ingredients: {
    id: string
    name: string
    qty: number
    unit: string
    ratio?: number | null
    /** tag 는 대체재 옆 꼬리표 (판매 문구 점검이 본다 — 앱 결과 화면에 칩으로 보인다) */
    substitutes: { name: string; tag?: string }[]
  }[]
  steps: {
    title: string
    desc: string
    temp: string
    time: string
    equipment: string
    qc: string
  }[]
  plainSteps: string[]
  estPh: string
  complianceSummary: string
  baseNote: string
  caution: string
}

/** 생성 요청의 분류 (input.majorId·subId). 결과에는 없어서 따로 받는다. 없으면 이름·세부유형 글로만 판정 */
export interface CertInputContext {
  majorId?: string | null
  subId?: string | null
}

/** 해썹 개별 기준표를 고르는 식품 종류 (식품공전 제5.12 장류 · 제5.13 소스) */
export type FoodKind =
  'gochujang' | 'doenjang' | 'jang-other' | 'mixed-jang' | 'soy-sauce' | 'sauce' | 'other'

export interface HalalMatch {
  ingredientId: string
  /** 걸린 원료 단위 글자 (관리자 근거용) */
  unit: string
  word: string
  term: IngredientTerm
  /** 대체재 이름에서 걸렸는지 */
  viaSubstitute: boolean
}

export interface HeatStep {
  index: number
  /** 범위면 낮은 쪽 (보수적으로) */
  tempC: number | null
  minutes: number | null
}

/** 공정 글(단계 제목·설명·설비)에서 찾은 금지 원료 (재료 칸에 없이 공정에서만 쓰는 술·돼지 원료) */
export interface HalalStepHit {
  stepIndex: number
  word: string
  term: IngredientTerm
}

export interface CertFacts {
  subject: CertSubject
  goals: CertGoals
  halalMarket: HalalMarket
  foodKind: FoodKind
  isJang: boolean
  /** 제품 자체가 술 (주류 분류, 또는 음료인데 이름·세부유형이 술). MUI Fatwa 10/2018: 에탄올 0.5% 이상 발효 음료는 khamr */
  isAlcoholicDrink: boolean
  /**
   * 레토르트식품 (식품공전 제4.2: 파우치 등 성형 용기에 충전·밀봉 후 가열 살균·멸균, 12개월 넘게 실온 유통).
   * '레토르트' 라는 말이 없어도 멸균 + 파우치·트레이 + 실온 + 소비기한 365일 초과면 레토르트다
   */
  isRetort: boolean
  /** 멸균 제품 (레토르트·멸균·고압살균 표기) */
  isSterilized: boolean
  /** 멸균해야 하는 유형 (통조림·병조림·레토르트, 식품공전 제4.1·4.2). pH 4.6 이하면 살균으로 만들 수 있다 (HK-CCP-03) */
  requiresSterilization: boolean
  /** 살균 공정이 있는 제품 (살균·열간충전·저온살균 표기가 있는 단계) */
  isPasteurized: boolean
  isDairy: boolean
  /** 축산물 HACCP 대상 영업 (식육가공업·유가공업·알가공업) */
  isLivestock: boolean
  isBeverage: boolean
  isPowder: boolean
  /** 발효로 알코올이 생길 수 있는 제품 (장류·간장·식초·김치·발효음료) */
  isFermented: boolean
  /** 고기·가금 원료가 있는지 (H-ING-06 사전) */
  hasMeat: boolean
  heatSteps: HeatStep[]
  /** 해동 단계가 있는지 */
  hasThaw: boolean
  hasGrinding: boolean
  /** 실온 유통이 금지된 유형인지 (양념젓갈·가공두부·두유·어육가공품·발효유·치즈 … — HK-STO-04) */
  coldChainType: boolean
  /** 0~5℃ 로 보관해야 하는 유형 (샐러드·훈제연어·액란·신선편의식품·가금육, 식품공전 2.4-2·별표1 I-33) */
  chill5Type: boolean
  halalMatches: HalalMatch[]
  halalStepHits: HalalStepHit[]
  /** 점검 뒤 배합·공정을 고쳤는지 (저장 재계산에서만 참. H-DOC-03, HK-PLAN-12) */
  edited: boolean
}

const productText = (s: CertSubject) =>
  [s.product.name, s.product.subtype, s.product.format].join(' ').normalize('NFKC')

const stepText = (step: CertSubject['steps'][number]) =>
  [step.title, step.desc, step.temp, step.time, step.equipment, step.qc].join(' ')

const SAUCE_TEXT = /소스|드레싱|케첩|마요네즈|sauce|dressing|ketchup|mayonnaise|ソース|酱汁/iu

/**
 * 분류(input)와 이름·세부유형 글로 식품 종류를 정한다. 분류가 있으면 분류만 본다 —
 * '고추장 불고기 덮밥'(식사)·'고추장 바비큐 소스'(소스)를 이름 때문에 고추장 기준으로 재면 지킬 수 없는 불합격이 된다.
 * 이름으로 장을 고르는 것은 분류가 없을 때(분석 모드·옛 행)이고, 그때도 발효·소스 분류일 때만이다
 */
export function foodKindOf(subject: CertSubject, input: CertInputContext = {}): FoodKind {
  const sub = `${input.majorId ?? ''}.${input.subId ?? ''}`
  if (sub === 'jang.gochujang' || sub === 'sauce.aged-gochujang') return 'gochujang'
  if (sub === 'jang.doenjang') return 'doenjang'
  if (sub === 'jang.ganjang' || sub === 'sauce.ganjang') return 'soy-sauce'
  if (sub === 'jang.ssamjang' || sub === 'sauce.ssamjang') return 'mixed-jang'
  if (sub === 'jang.other' || sub === 'sauce.jang') return 'jang-other'
  const text = productText(subject)
  const category = subject.product.categoryId
  const sauce = category === 'sauce' || SAUCE_TEXT.test(text)
  if (input.majorId) return sauce ? 'sauce' : 'other'
  if (category !== 'fermented' && category !== 'sauce') return 'other'
  // 초고추장·간장 소스·쌈장 드레싱처럼 장을 섞어 바로 쓰는 양념은 소스다
  // ('soy sauce' 는 간장 이름이라 소스 말에서 뺀다)
  if (
    SAUCE_TEXT.test(text.replace(/soy\s?sauce/giu, '')) ||
    /초고추장|간장\s?양념|soy[\s-]sauce[\s-]based|teriyaki/iu.test(text)
  )
    return 'sauce'
  if (/쌈장|혼합장|ssamjang/iu.test(text)) return 'mixed-jang'
  if (/고추장|gochujang|コチュジャン|韩式辣椒酱/iu.test(text)) return 'gochujang'
  if (/된장|doenjang|韓国味噌|大酱/iu.test(text)) return 'doenjang'
  if (/춘장|청국장|막장|chunjang|cheonggukjang/iu.test(text)) return 'jang-other'
  if (/간장|soy sauce|ganjang|醤油|酱油/iu.test(text)) return 'soy-sauce'
  return sauce ? 'sauce' : 'other'
}

// ── 온도·시간 읽기 ─────────────────────────────────────────────────

const NUM = String.raw`-?\d+(?:\.\d+)?`
const TEMP_RE = new RegExp(
  String.raw`(${NUM})(?:\s*(?:~|–|-|—|to)\s*(${NUM}))?\s*(℃|°\s*C|°\s*F|도(?![a-z가-힣])|度|ºC|C(?![a-z]))`,
  'giu',
)

/** 글에서 온도들을 ℃ 로 읽는다. "85–88℃" → [85, 88], "180°F" → [82.2], "상온" → [] */
export function parseTemperatures(text: string): number[] {
  const out: number[] = []
  for (const m of text.normalize('NFKC').matchAll(TEMP_RE)) {
    const unit = m[3]!.replace(/\s/g, '').toUpperCase()
    const toC = (v: number) => (unit === '°F' ? ((v - 32) * 5) / 9 : v)
    out.push(toC(Number(m[1])))
    if (m[2]) out.push(toC(Number(m[2])))
  }
  return out.map((v) => Math.round(v * 10) / 10)
}

const TIME_UNITS: [RegExp, number][] = [
  [/^(?:시간|h|hr|hrs|hour|hours|時間|小时|小時)$/iu, 60],
  [/^(?:분|m|min|mins|minute|minutes|分|分钟|分鐘)$/iu, 1],
  [/^(?:초|s|sec|secs|second|seconds|秒|秒钟)$/iu, 1 / 60],
]
const TIME_RE = new RegExp(
  String.raw`(${NUM})(?:\s*(?:~|–|-|—|to)\s*(${NUM}))?\s*(시간|hours?|hrs?|h(?![a-z])|분|minutes?|mins?|m(?![a-z])|초|seconds?|secs?|s(?![a-z])|時間|小时|小時|分钟|分鐘|分|秒钟|秒)`,
  'giu',
)

/** 글에서 시간들을 분으로 읽는다. "15–30초" → [0.25, 0.5], "1시간 30분" → [60, 30] (합치지 않는다) */
export function parseDurations(text: string): number[] {
  const out: number[] = []
  for (const m of text.normalize('NFKC').matchAll(TIME_RE)) {
    const factor = TIME_UNITS.find(([re]) => re.test(m[3]!))?.[1]
    if (factor === undefined) continue
    out.push(Number(m[1]) * factor)
    if (m[2]) out.push(Number(m[2]) * factor)
  }
  return out.filter((v) => v >= 0)
}

/** 단계의 대표 온도·시간 (temp·time 칸이 먼저, 비면 qc·desc 에서). 범위는 낮은 쪽 */
export function stepTempTime(step: CertSubject['steps'][number]): {
  tempC: number | null
  minutes: number | null
} {
  const temps = parseTemperatures(step.temp)
  const times = parseDurations(step.time)
  const fallbackTemps = temps.length ? temps : parseTemperatures(`${step.qc} ${step.desc}`)
  const fallbackTimes = times.length ? times : parseDurations(`${step.qc} ${step.desc}`)
  const tempC = fallbackTemps.length ? Math.min(...fallbackTemps.filter((t) => t > 0)) : null
  const minutes = fallbackTimes.length ? Math.min(...fallbackTimes) : null
  return {
    tempC: tempC !== null && Number.isFinite(tempC) ? tempC : null,
    minutes: minutes !== null && Number.isFinite(minutes) ? minutes : null,
  }
}

/** estPh 의 숫자 (범위면 높은 쪽 — pH 4.6 이하인지 볼 때 보수적으로). 숫자가 없으면 null */
export function parsePh(estPh: string): number | null {
  const nums = [...estPh.normalize('NFKC').matchAll(/(\d+(?:\.\d+)?)/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n > 0 && n <= 14)
  return nums.length ? Math.max(...nums) : null
}

// ── 사실 계산 ────────────────────────────────────────────────────

/** 멸균 공정·제품 표기 */
const STERILIZED_WORDS = [
  '레토르트',
  '멸균',
  '고압살균',
  'retort',
  'steriliz',
  'sterilis',
  'autoclave',
  'レトルト',
  '滅菌',
  '灭菌',
  '蒸煮袋',
]
const RETORT_WORDS = ['레토르트', 'retort', 'レトルト', '蒸煮袋']
/** 멸균해야 하는 유형: 통·병조림 (식품공전 제4.1). 살균만 한 산성 통조림은 HK-CCP-03 이 본다 */
const CANNED_WORDS = ['통조림', '병조림', 'canned', 'canning', '缶詰', '瓶詰', '罐头']
/** 파우치 등 성형 용기 (레토르트 정의의 용기) */
const POUCH_FORMAT = /파우치|pouch|성형\s?용기|트레이|tray|パウチ|袋/iu
const PASTEURIZE_WORDS = [
  '살균',
  '열간충전',
  '저온살균',
  '핫필',
  'pasteur',
  'hot-fill',
  'hot fill',
  'hotfill',
  '殺菌',
  '杀菌',
  '巴氏',
]
const THAW_WORDS = ['해동', 'thaw', 'defrost', '解凍', '解冻']
const GRIND_WORDS = [
  '분쇄',
  '제분',
  '갈아',
  '밀링',
  'grind',
  'mill',
  '粉砕',
  '粉碎',
  '研磨',
  '磨粉',
]
/**
 * 실온 유통 금지 유형 (식품공전 제2.4-2(4), HK-STO-04): 양념젓갈·가공두부·두유·어육가공품·발효유·액란·샐러드·훈제연어·
 * 신선편의식품. 치즈·버터·우유 같은 유가공품은 이름이 아니라 유제품 분류로 본다 ('땅콩버터'·'치즈케이크' 는 아니다)
 */
const COLD_CHAIN_TYPE =
  /양념젓갈|두부|두유|어묵|어육|발효유|요거트|요구르트|yogh?urt|액란|샐러드|salad|훈제\s?연어|smoked salmon|신선편의|tofu|soy\s?milk|fish\s?cake|豆腐|豆乳|かまぼこ|ヨーグルト|酸奶|鱼糕|鱼丸/iu
/** 0~5℃ 유형 (샐러드·훈제연어·액란 식품공전 2.4-2, 신선편의식품·가금육 별표1 I-33). 가금육은 날것만 (닭가슴살 소시지는 식육가공품) */
const CHILL_5_TYPE =
  /샐러드|salad|훈제\s?연어|smoked salmon|액란|liquid egg|신선편의|가금육|생닭|생오리|raw poultry|サラダ|沙拉/iu
/** 요리 이름이 붙은 양념·소스 ('순두부찌개 양념', '어묵탕 스프', '샐러드 드레싱') 는 그 요리 유형이 아니다 */
const SEASONING_NAME =
  /양념(?!젓갈)|소스|스프|육수|베이스|시즈닝|드레싱|seasoning|sauce|soup base|dressing|ソース|调料|酱/iu
/** 유가공품 중 부패를 막도록 가공해 실온이 되는 것 (멸균·당장·건조, 식품공전 2.4-2(5)) */
const SHELF_STABLE_DAIRY = /연유|condensed|분유|milk powder|uht|멸균/iu
/**
 * 제품 자체가 술인지 (음료 분류일 때 이름·세부유형으로). 루트비어·진저비어는 탄산음료, 식초는 식초다.
 * 무알코올 맥주·와인도 술에서 온 음료라 할랄이 아니다 (MUI Fatwa 10/2018 제3부 4항)
 */
const ALCOHOLIC_DRINK =
  /막걸리|약주|탁주|소주|맥주|와인|포도주|사케|리큐어|위스키|보드카|하이볼|칵테일|liquor|liqueur|beer|wine|sake|soju|makgeolli|whiske?y|vodka|cocktail|ビール|ワイン|啤酒|葡萄酒|酒/iu
const NOT_ALCOHOLIC_DRINK = /루트\s?비어|진저\s?비어|root beer|ginger beer|식초|vinegar|酢|醋/iu

export function deriveCertFacts(
  subject: CertSubject,
  goals: CertGoals,
  options: { input?: CertInputContext; edited?: boolean } = {},
): CertFacts {
  const foodKind = foodKindOf(subject, options.input)
  const text = productText(subject)
  const stepsText = subject.steps.map(stepText).join(' ')
  const { storage, shelfLifeDays, format } = subject.product
  const sterilizedWords = textHasAny(`${text} ${stepsText}`, STERILIZED_WORDS)
  const isRetort =
    textHasAny(`${text} ${stepsText}`, RETORT_WORDS) ||
    (sterilizedWords && POUCH_FORMAT.test(format) && storage === 'ambient' && shelfLifeDays > 365)
  const isSterilized = isRetort || sterilizedWords
  const heatSteps = subject.steps
    .map((step, index) => ({ index, ...stepTempTime(step) }))
    .filter((s) => s.tempC !== null && s.tempC >= 60)
  const halalMatches: HalalMatch[] = []
  for (const ing of subject.ingredients) {
    const names = [
      { name: ing.name, viaSubstitute: false },
      ...ing.substitutes.map((s) => ({ name: s.name, viaSubstitute: true })),
    ]
    for (const { name, viaSubstitute } of names) {
      for (const unit of splitIngredientUnits(name)) {
        const hit = matchTerm(unit, HALAL_TERMS)
        if (hit)
          halalMatches.push({
            ingredientId: ing.id,
            unit: unit.text,
            word: hit.word,
            term: hit.term,
            viaSubstitute,
          })
      }
    }
  }
  // 공정 글에서만 쓰는 금지 원료. 같은 규칙에 이미 걸린 재료가 있으면 그 재료 항목으로 충분하다
  const failTerms = HALAL_TERMS.filter((t) => t.action === 'fail')
  const halalStepHits: HalalStepHit[] = subject.steps.flatMap((step, stepIndex) =>
    textTermHits(`${step.title}\n${step.desc}\n${step.equipment}`, failTerms)
      .filter(
        (hit) => !halalMatches.some((m) => m.term.ruleId === hit.term.ruleId && !m.viaSubstitute),
      )
      .map((hit) => ({ stepIndex, ...hit })),
  )
  const category = subject.product.categoryId
  const isBeverage = category === 'beverage' || category === 'juice' || category === 'alcohol'
  const nameAndType = `${subject.product.name} ${subject.product.subtype}`.normalize('NFKC')
  const isPowder = /분말|가루|파우더|환$|powder|粉末|パウダー|粉/iu.test(
    `${subject.product.subtype} ${format}`,
  )
  const seasoning = SEASONING_NAME.test(nameAndType)
  const isPasteurized = !isSterilized && textHasAny(stepsText, PASTEURIZE_WORDS)
  return {
    subject,
    goals,
    halalMarket: goals.halalMarket ?? DEFAULT_HALAL_MARKET,
    foodKind,
    isJang: ['gochujang', 'doenjang', 'jang-other', 'mixed-jang'].includes(foodKind),
    isAlcoholicDrink:
      category === 'alcohol' ||
      (isBeverage && ALCOHOLIC_DRINK.test(nameAndType) && !NOT_ALCOHOLIC_DRINK.test(nameAndType)),
    isRetort,
    isSterilized,
    requiresSterilization: isRetort || textHasAny(`${text} ${stepsText}`, CANNED_WORDS),
    isPasteurized,
    isDairy: category === 'dairy',
    isLivestock: category === 'dairy' || category === 'processed-meat',
    isBeverage,
    // 제품 이름은 보지 않는다 ('고춧가루 듬뿍 고추장' 은 분말이 아니다)
    isPowder,
    isFermented:
      foodKind !== 'sauce' && foodKind !== 'other'
        ? true
        : category === 'fermented' ||
          /발효|식초|김치|콤부차|ferment|vinegar|kimchi|kombucha|発酵|发酵/iu.test(text),
    hasMeat: halalMatches.some((m) => m.term.ruleId === 'H-ING-06'),
    heatSteps,
    hasThaw: textHasAny(
      `${stepsText} ${subject.ingredients.map((i) => i.name).join(' ')}`,
      THAW_WORDS,
    ),
    hasGrinding: textHasAny(stepsText, GRIND_WORDS),
    // 이름·세부유형·용기로 유형을 본다. 요리 이름이 붙은 양념은 뺀다. 유가공품은 분류로 (멸균·연유·분유 제외)
    coldChainType:
      (!seasoning && COLD_CHAIN_TYPE.test(text)) ||
      (category === 'dairy' && !isSterilized && !isPowder && !SHELF_STABLE_DAIRY.test(text)),
    chill5Type: !seasoning && CHILL_5_TYPE.test(nameAndType),
    halalMatches,
    halalStepHits,
    edited: options.edited === true,
  }
}
